import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, UtensilsCrossed } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AuthImage } from '@/components/auth-image';
import { ImageLightbox } from '@/components/image-lightbox';
import { cn } from '@/lib/utils';

/**
 * A cafe/restaurant menu as photos (client change, 9 Oct 2026): one page at a time, swipe or
 * use the left/right arrows, tap to open full screen. Replaces the typed menu + cart — ordering
 * happens at the venue.
 */
export function MenuImagesViewer({ images }: { images: { id: string; file_path: string }[] }) {
  const [index, set_index] = useState(0);
  const [open, set_open] = useState(false);
  const touch_x = useRef<number | null>(null);
  if (images.length === 0) return null;

  const current = images[Math.min(index, images.length - 1)]!;
  const go = (by: -1 | 1) => set_index((i) => Math.min(images.length - 1, Math.max(0, i + by)));

  return (
    <Card data-testid="menu-images-viewer">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UtensilsCrossed className="h-5 w-5 text-primary" /> Menu
          <span className="ml-auto text-sm font-normal text-muted-foreground" data-testid="menu-page-counter">
            {index + 1} / {images.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 p-4 pt-0">
        <div
          className="relative"
          onTouchStart={(e) => (touch_x.current = e.touches[0]?.clientX ?? null)}
          onTouchEnd={(e) => {
            const start = touch_x.current;
            const end = e.changedTouches[0]?.clientX;
            touch_x.current = null;
            if (start == null || end == null || Math.abs(end - start) < 40) return;
            go(end < start ? 1 : -1);
          }}
        >
          <button
            type="button"
            onClick={() => set_open(true)}
            className="flex min-h-64 w-full items-center justify-center overflow-hidden rounded-md border-2 border-input bg-muted"
            aria-label="Open menu full screen"
          >
            <AuthImage
              path={`${current.file_path}?variant=medium`}
              alt={`Menu page ${index + 1}`}
              className="mx-auto max-h-[70vh] w-full object-contain"
              data-testid="menu-page-image"
            />
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                disabled={index === 0}
                aria-label="Previous page"
                className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-input bg-card shadow-clay-sm disabled:opacity-40"
                data-testid="menu-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                disabled={index === images.length - 1}
                aria-label="Next page"
                className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-input bg-card shadow-clay-sm disabled:opacity-40"
                data-testid="menu-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {images.map((img, i) => (
              <button
                key={img.id}
                type="button"
                onClick={() => set_index(i)}
                aria-label={`Menu page ${i + 1}`}
                className={cn(
                  'h-16 w-12 shrink-0 overflow-hidden rounded-md border-2',
                  i === index ? 'border-primary' : 'border-input',
                )}
              >
                <AuthImage path={`${img.file_path}?variant=thumb`} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Order at the venue — prices may change.</p>
      </CardContent>

      {open && (
        <ImageLightbox paths={images.map((i) => i.file_path)} index={index} on_close={() => set_open(false)} on_navigate={set_index} />
      )}
    </Card>
  );
}
